import { useState, useEffect, Fragment, memo } from 'react';
import Text from '@deprecated-ui/common/Text';
import Flex from '@deprecated-ui/common/Flex';
import Select from '@deprecated-ui/common/Select';
import Input from '@deprecated-ui/common/Input';
import color from '@deprecated-ui/_style/color';
import { useSelector } from 'react-redux';
import { screenerSelectors } from 'features/screener/slice';
import DATA_CY from 'constants/data-cy';

const { Option: SelectOption } = Select;
const ScreenerStocks = ({
  defaultValue,
  onSelectUniverse,
}: {
  defaultValue: { scope: string; scopeID: string };
  onSelectUniverse: (scope: string, scopeID: string) => void;
}) => {
  const [filteredSectors, setFilteredSectors] = useState(null);
  const [filteredIndex, setFilteredIndex] = useState(null);
  const [filteredWatchlist, setFilteredWatchlist] = useState(null);
  const [selectedSector, setSelectedSector] = useState('IHSG');

  const {
    screenerUniverse: { data },
  } = useSelector(screenerSelectors);

  const { sectors, index, watchlist } = data;

  const filterIndex = (query: string) => {
    const lowerCaseQuery = query.toLowerCase();
    const { list, name } = index[0];
    if (name.toLowerCase().includes(lowerCaseQuery)) {
      return index;
    }

    const filteredList = list.filter((el) =>
      el.name.toLowerCase().includes(lowerCaseQuery),
    );
    return [{ name, list: filteredList }];
  };

  const filterSector = (query: string) => {
    const lowerCaseQuery = query.toLowerCase();
    return sectors.filter((el) =>
      JSON.stringify(el).toLowerCase().includes(lowerCaseQuery),
    );
  };

  const filterWatchlist = (query: string) => {
    const lowerCaseQuery = query.toLowerCase();
    return watchlist.filter((el) =>
      el.name.toLowerCase().includes(lowerCaseQuery),
    );
  };

  const onTypeHandler = (e) => {
    const query: string = e.target.value;
    if (query.length !== 0) {
      const foundSector = filterSector(query);
      const foundIndex = filterIndex(query);
      const foundWatchlist = filterWatchlist(query);

      setFilteredSectors(foundSector);
      setFilteredIndex(foundIndex);
      setFilteredWatchlist(foundWatchlist);
    } else {
      setFilteredSectors(null);
      setFilteredIndex(null);
      setFilteredWatchlist(null);
    }
  };

  const dropdownSectorSearch = (dropdownItem) => (
    <>
      <Input
        // @ts-expect-error
        mx="8px"
        width="calc(100% - 16px)"
        onChange={onTypeHandler}
        data-cy={DATA_CY.SCREENER.STOCK_SEARCH_INPUT}
      />
      {dropdownItem}
    </>
  );

  const onSelectHandler = (val: string) => {
    const [name, id] = val.split('.');
    setSelectedSector(val);
    onSelectUniverse(name, id);
  };

  const recomposeSectors = () => {
    const sectorsOption = [
      // @ts-expect-error
      <SelectOption key="sector-title" disabled>
        Sectors
      </SelectOption>,
    ];

    if (filteredSectors) {
      filteredSectors?.forEach(
        ({ name, scope: scopeSect, id: idSect, subsector }) => {
          sectorsOption.push(
            <Fragment key={`${scopeSect}-${idSect}`}>
              <SelectOption
                value={`${scopeSect}.${idSect}`}
                key={`${scopeSect}-${idSect}`}
                style={{ paddingLeft: '30px' }}
              >
                {name}
              </SelectOption>

              {subsector.map(
                ({ id: idSub, name: subSectorName, scope: scopeSub }) => (
                  <SelectOption
                    value={`${scopeSub}.${idSub}`}
                    key={`${scopeSub}-${idSub}`}
                    style={{ paddingLeft: '46px' }}
                  >
                    {subSectorName}
                  </SelectOption>
                ),
              )}
            </Fragment>,
          );
        },
      );
    } else {
      sectors?.forEach(({ name, scope: scopeSect, id: idSect, subsector }) => {
        sectorsOption.push(
          <Fragment key={`${scopeSect}-${idSect}`}>
            <SelectOption
              value={`${scopeSect}.${idSect}`}
              key={`${scopeSect}-${idSect}`}
              style={{ paddingLeft: '30px' }}
            >
              {name}
            </SelectOption>

            {subsector.map(
              ({ id: idSub, name: subSectorName, scope: scopeSub }) => (
                <SelectOption
                  value={`${scopeSub}.${idSub}`}
                  key={`${scopeSub}-${idSub}`}
                  style={{ paddingLeft: '46px' }}
                >
                  {subSectorName}
                </SelectOption>
              ),
            )}
          </Fragment>,
        );
      });
    }
    return sectorsOption;
  };

  const recomposeIndex = () => {
    const indexOption = [
      // @ts-expect-error
      <SelectOption key="index-title" disabled>
        Index
      </SelectOption>,
    ];

    if (filteredIndex) {
      filteredIndex.forEach(({ name, id, scope, list }) => {
        if (list.length > 0) {
          indexOption.push(
            <Fragment key={`${id}-${name}`}>
              <SelectOption
                value={`${scope}.${id}`}
                key={`${scope}.${id}`}
                style={{ paddingLeft: '30px' }}
              >
                {name}
              </SelectOption>
              {list.map(({ id: listId, name: listName, scope: listScope }) => (
                <SelectOption
                  key={`${listScope}.${listId}`}
                  style={{ paddingLeft: '46px' }}
                  value={`${listScope}.${listId}`}
                >
                  {listName}
                </SelectOption>
              ))}
            </Fragment>,
          );
        }
      });
    } else {
      index?.forEach(({ name, list, id, scope }) => {
        indexOption.push(
          <Fragment key={`${id}-${name}`}>
            <SelectOption
              value={`${scope}.${id}`}
              key={`${scope}.${id}`}
              style={{ paddingLeft: '30px' }}
            >
              {name}
            </SelectOption>
            {list.map(({ id: listId, name: listName, scope: listScope }) => (
              <SelectOption
                key={`${listScope}.${listId}`}
                style={{ paddingLeft: '46px' }}
                value={`${listScope}.${listId}`}
              >
                {listName}
              </SelectOption>
            ))}
          </Fragment>,
        );
      });
    }
    return indexOption;
  };

  const recomposeWatchlist = () => {
    const watchlistOption = [
      // @ts-expect-error
      <SelectOption key="watchlist-title" disabled>
        Watchlist
      </SelectOption>,
    ];

    if (filteredWatchlist) {
      filteredWatchlist.forEach(({ name, id, scope }) => {
        watchlistOption.push(
          <SelectOption
            key={`${scope}.${id}`}
            value={`${scope}.${id}`}
            style={{ paddingLeft: '30px' }}
          >
            {name}
          </SelectOption>,
        );
      });
    } else {
      watchlist?.forEach(({ name, id, scope }) => {
        watchlistOption.push(
          <Fragment key={`${id}-${name}`}>
            <SelectOption
              value={`${scope}.${id}`}
              key={`${scope}.${id}`}
              style={{ paddingLeft: '30px' }}
            >
              {name}
            </SelectOption>
          </Fragment>,
        );
      });
    }

    return watchlistOption;
  };

  const handleOptions = () => (
    <>
      {recomposeIndex()}
      {recomposeSectors()}
      {recomposeWatchlist()}
    </>
  );

  useEffect(() => {
    const { scope, scopeID } = defaultValue;
    if (scopeID !== '') {
      setSelectedSector(`${scope}.${scopeID}`);
    } else {
      setSelectedSector(`${scope}`);
    }
  }, [defaultValue]);

  useEffect(() => {
    handleOptions();
  }, [filteredSectors, filteredIndex, filteredWatchlist]);

  return (
    // @ts-expect-error
    <Flex direction="column" mb="15px" width="100%">
      {/* @ts-expect-error */}
      <Text color={color.dimgray} weight="700" as="h3" pb="10px" family="bold">
        Stock Universe
      </Text>
      <Select
        // @ts-expect-error
        name="stockUniverse"
        width="300px"
        dropdownRender={dropdownSectorSearch}
        showArrow
        onChange={onSelectHandler}
        value={selectedSector}
        onSearch={onTypeHandler}
      >
        {handleOptions()}
      </Select>
    </Flex>
  );
};

// I've test this memo, and its necessary
export default memo(ScreenerStocks);
